import React, { isValidElement, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { StyledGridComposerActionsContainer, StyledGridComposerContainer, StyledGridComposerInputContainer, StyledComposerMarkdownPreview, StyledTriggerMenuEmpty, StyledTriggerMenuItem, StyledTriggerMenuItemText, StyledTriggerMenuItemAnnotation } from './styles';
import { type ComposerMenuItem, type MessageComposerProps, type SlashCommand } from './types';
import { buildMentionChipHtml, buildSlashCommandChipHtml, extractSlashCommandIds, filterComposerMenuItems, getCaretCharacterOffset, getChipElements, getClipboardFilesToAttach, getSelectionRangeInside, getTriggerQuery, htmlToMarkdown, MENTION_CHIP_CLASS_NAME, MENTION_TRIGGER_REGEX, navigateTriggerMenu, placeCaretAfterNewChip, preventBlur, restoreCaret, selectTriggerTokenAtCaret, SLASH_COMMAND_CHIP_CLASS_NAME, SLASH_TRIGGER_REGEX } from './utils';
import { ComposerTriggerMenu } from './ComposerTriggerMenu';
import { ArrowRightIcon } from '../../icons/ArrowRightIcon';
import { ChainLinkIcon } from '../../icons/ChainLinkIcon';
import { BoldIcon } from '../../icons/BoldIcon';
import { ItalicIcon } from '../../icons/ItalicIcon';
import { MentionsIcon } from '../../icons/MentionsIcon';
import { PauseIcon } from '../../icons/PauseIcon';
import Button from '../../Button';
import Tooltip from '../../Tooltip';
import { useTheme } from '../../../utils/useTheme';
import { getInlineChipCssText } from '../../InlineChip/styles';
import InsertLinkPopup from './InsertLinkPopup';

/** Menu row plus the optional chip colour; `Mention` is a structural subset. */
type TriggerMenuItem = ComposerMenuItem & Pick<SlashCommand, 'chipColor'>;
const CHIP_TOOLTIP_DELAY_IN_MS = 500;
const getTooltipBody = (tooltip: SlashCommand['tooltip']): string | React.ReactElement | undefined => (typeof tooltip === 'string' || isValidElement(tooltip)) && tooltip !== '' ? tooltip : undefined;

/** One caret-anchored menu: `@` mentions or `/` commands differ only by this config. */
interface ComposerTrigger {
  char: '@' | '/';
  /** Must capture the leading boundary in group 1 (see `getTriggerIndex`). */
  regex: RegExp;
  items: readonly TriggerMenuItem[];
  menuWidth: string;
  menuMaxHeight: string;
  chipClassName: string;
  buildChipHtml: (item: TriggerMenuItem) => string;
  /** Shown instead of the list while `items` is empty; without it the trigger is inactive then. */
  emptyMessage?: string;
}
export const MessageComposer = ({
  ref,
  inputPlaceholder,
  onSubmit,
  onBeforeSubmit,
  actions,
  onCancel,
  mentions = [],
  slashCommands = [],
  slashCommandsEmptyMessage,
  shouldShowDefaultActions = false,
  isSubmitting = false,
  isInternalMessage = false,
  elevated = false,
  attachmentSlot,
  disableSubmit = false,
  onPasteFiles
}: MessageComposerProps): React.ReactElement => {
  const theme = useTheme();
  const editorRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [activeTrigger, setActiveTrigger] = useState<{
    char: ComposerTrigger['char'];
    query: string;
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<{
    getBoundingClientRect: () => DOMRect;
  } | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isBoldActive, setIsBoldActive] = useState(false);
  const [isItalicActive, setIsItalicActive] = useState(false);
  const [openLinkPopup, setOpenLinkPopup] = useState(false);
  const [linkInitialText, setLinkInitialText] = useState('');
  const savedRangeRef = useRef<Range | null>(null);
  // Last caret position inside the editor; chips inserted from outside (Skills menu) go there.
  const lastCaretRangeRef = useRef<Range | null>(null);
  // Slash-command chips are plain HTML inside the contenteditable, so their hover tooltip is
  // mounted separately, anchored to an invisible box over the hovered chip.
  const [chipTooltip, setChipTooltip] = useState<{
    command: SlashCommand;
    rect: DOMRect;
  } | null>(null);
  const hoveredChipRef = useRef<HTMLElement | null>(null);
  const chipTooltipTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hideChipTooltip = (): void => {
    clearTimeout(chipTooltipTimerRef.current);
    hoveredChipRef.current = null;
    setChipTooltip(null);
  };
  useEffect(() => () => {
    clearTimeout(chipTooltipTimerRef.current);
  }, []);
  const handleEditorMouseOver = (event: React.MouseEvent<HTMLDivElement>): void => {
    const chip = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-slash-command-id]') : null;
    if (chip === hoveredChipRef.current) return;
    hideChipTooltip();
    const command = slashCommands.find(item => String(item.id) === chip?.dataset.slashCommandId);
    if (chip == null || command == null || getTooltipBody(command.tooltip) == null) return;
    hoveredChipRef.current = chip;
    chipTooltipTimerRef.current = setTimeout(() => {
      setChipTooltip({
        command,
        rect: chip.getBoundingClientRect()
      });
    }, CHIP_TOOLTIP_DELAY_IN_MS);
  };
  useImperativeHandle(ref, () => ({
    focus: () => {
      focusAtLastCaret();
    },
    insertSlashCommand: (command_0: Pick<SlashCommand, 'id' | 'name' | 'chipColor'>) => {
      insertChip(buildSlashCommandChipHtml(command_0, getInlineChipCssText(theme, command_0.chipColor)));
    }
  }));

  // Order matters: `@` wins when both could match under the caret.
  const triggers: ComposerTrigger[] = [{
    char: '@' as const,
    regex: MENTION_TRIGGER_REGEX,
    items: mentions,
    menuWidth: '220px',
    menuMaxHeight: 'min(240px, 50vh)',
    chipClassName: MENTION_CHIP_CLASS_NAME,
    buildChipHtml: buildMentionChipHtml
  }, {
    char: '/' as const,
    regex: SLASH_TRIGGER_REGEX,
    items: slashCommands,
    menuWidth: '360px',
    menuMaxHeight: 'min(320px, 50vh)',
    chipClassName: SLASH_COMMAND_CHIP_CLASS_NAME,
    buildChipHtml: (item_0: TriggerMenuItem) => buildSlashCommandChipHtml(item_0, getInlineChipCssText(theme, item_0.chipColor)),
    emptyMessage: slashCommandsEmptyMessage
  }].filter((trigger: ComposerTrigger) => trigger.items.length > 0 || (trigger.emptyMessage ?? '') !== '');
  const activeTriggerConfig = triggers.find(trigger_0 => trigger_0.char === activeTrigger?.char);
  const menuItems = activeTrigger == null || activeTriggerConfig == null ? [] : filterComposerMenuItems(activeTriggerConfig.items, activeTrigger.query);
  const emptyMessage = activeTriggerConfig?.items.length === 0 ? activeTriggerConfig.emptyMessage : undefined;
  const isMenuOpen = menuItems.length > 0 || (emptyMessage ?? '') !== '';

  // Stable callback ref: scrolls only when the highlighted row changes.
  const scrollHighlightedItemIntoView = useCallback((node: HTMLLIElement | null): void => {
    node?.scrollIntoView({
      block: 'nearest'
    });
  }, []);
  const getCaretAnchor = (): {
    getBoundingClientRect: () => DOMRect;
  } | null => {
    const selection = globalThis.getSelection();
    if (selection == null || selection.rangeCount === 0) return null;
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    return {
      getBoundingClientRect: () => rect
    };
  };
  const openMenu = (char: ComposerTrigger['char'], query: string): void => {
    setActiveTrigger({
      char,
      query
    });
    setAnchorEl(getCaretAnchor());
    setHighlightedIndex(0);
  };
  const closeMenu = (): void => {
    setActiveTrigger(null);
    setAnchorEl(null);
    setHighlightedIndex(0);
  };

  // Tracked while the user edits: by the time `blur` fires, focus (and the selection)
  // may already have moved to another field.
  const rememberCaret = (): void => {
    const range = editorRef.current == null ? null : getSelectionRangeInside(editorRef.current);
    if (range != null) {
      lastCaretRangeRef.current = range;
    }
  };

  /**
   * Focus the editor with the caret where the user left it (end when unknown).
   * The saved caret is read before `focus()`: Chrome moves the caret to the start on
   * focus, and a focus trap (e.g. the Skills popover) bouncing focus straight back
   * fires `blur`, which would otherwise re-save that start position.
   */
  const focusAtLastCaret = (): void => {
    const editor = editorRef.current;
    if (editor == null) return;
    if (document.activeElement === editor) return;
    const savedCaret = lastCaretRangeRef.current;
    editor.focus();
    const selection_0 = globalThis.getSelection();
    if (selection_0 != null) {
      restoreCaret(editor, selection_0, savedCaret);
      rememberCaret();
    }
  };
  const handleInput = (): void => {
    const editor_0 = editorRef.current;
    if (editor_0 == null) return;
    rememberCaret();
    hideChipTooltip();
    const text = editor_0.textContent?.trim() ?? '';
    setIsEmpty(text.length === 0);
    syncFormattingState();
    const fullText = editor_0.textContent ?? '';
    const cursorPos = getCaretCharacterOffset(editor_0);
    for (const trigger_1 of triggers) {
      const query_0 = getTriggerQuery(fullText, cursorPos, trigger_1.regex);
      if (query_0 != null) {
        openMenu(trigger_1.char, query_0);
        return;
      }
    }
    closeMenu();
  };
  const handleSubmit = (): void => {
    const editor_1 = editorRef.current;
    if (editor_1 == null || isEmpty || disableSubmit || isSubmitting) return;
    const html = editor_1.innerHTML;
    const markdown = htmlToMarkdown(html);
    const meta = {
      slashCommandIds: extractSlashCommandIds(html)
    };
    if (onBeforeSubmit?.(markdown, meta) === false) return;
    onSubmit(markdown, meta);
    hideChipTooltip();
    editor_1.innerHTML = '';
    setIsEmpty(true);
  };
  const isFormatActive = (format: 'bold' | 'italic'): boolean => {
    const selection_1 = globalThis.getSelection();
    if (selection_1 == null || selection_1.rangeCount === 0) return false;
    let node_0: Node | null = selection_1.anchorNode;
    while (node_0 != null && node_0 !== editorRef.current) {
      if (node_0 instanceof HTMLElement) {
        const tag = node_0.tagName.toLowerCase();
        if (format === 'bold' && (tag === 'b' || tag === 'strong')) return true;
        if (format === 'italic' && (tag === 'i' || tag === 'em')) return true;
      }
      node_0 = node_0.parentNode;
    }
    return false;
  };
  const syncFormattingState = (): void => {
    setIsBoldActive(isFormatActive('bold'));
    setIsItalicActive(isFormatActive('italic'));
  };
  const handleBold = (): void => {
    editorRef.current?.focus();
    document.execCommand('bold');
    setIsBoldActive(!isBoldActive);
  };
  const handleItalic = (): void => {
    editorRef.current?.focus();
    document.execCommand('italic');
    setIsItalicActive(!isItalicActive);
  };
  const handleInsertLink = (text_0: string, url: string): void => {
    const editor_2 = editorRef.current;
    if (editor_2 == null) return;
    editor_2.focus();
    const selection_2 = globalThis.getSelection();
    if (selection_2 != null && savedRangeRef.current != null) {
      selection_2.removeAllRanges();
      selection_2.addRange(savedRangeRef.current);
    }
    const linkHtml = `<a href="${url}">${text_0}</a>`;
    document.execCommand('insertHTML', false, linkHtml);
    savedRangeRef.current = null;
    setIsEmpty(false);
    setOpenLinkPopup(false);
  };

  /**
   * Insert chip HTML at the caret. With `trigger`, the in-progress `@query` / `/query`
   * under the caret is replaced so no leftover trigger text sits next to the chip.
   * Skills-button inserts pass no trigger so incidental paths like `/tmp` are kept.
   */
  const insertChip = (html_0: string, trigger_2?: ComposerTrigger): void => {
    const editor_3 = editorRef.current;
    if (editor_3 == null) return;
    // From outside (e.g. the Skills menu) this restores the caret the user left.
    focusAtLastCaret();
    const selection_3 = globalThis.getSelection();
    const replacesTrigger = trigger_2 != null && selection_3 != null && selectTriggerTokenAtCaret(selection_3, trigger_2.regex);
    const existingChips = trigger_2 != null && replacesTrigger ? getChipElements(editor_3, trigger_2.chipClassName) : null;
    document.execCommand('insertHTML', false, html_0);
    if (trigger_2 != null && selection_3 != null && existingChips != null) {
      placeCaretAfterNewChip(editor_3, selection_3, trigger_2.chipClassName, existingChips);
    }
    rememberCaret();
    closeMenu();
    setIsEmpty(false);
  };
  const handleMenuItemSelect = (item_1: TriggerMenuItem): void => {
    if (item_1.disabled === true || activeTriggerConfig == null) {
      return;
    }
    insertChip(activeTriggerConfig.buildChipHtml(item_1), activeTriggerConfig);
  };
  const handleMentionsTrigger = (): void => {
    const editor_4 = editorRef.current;
    if (editor_4 == null) return;
    editor_4.focus();
    const selection_4 = globalThis.getSelection();
    if (selection_4 == null || selection_4.rangeCount === 0) return;
    const range_0 = selection_4.getRangeAt(0);
    const container = range_0.startContainer;
    let needsSpace = false;
    if (container.nodeType === Node.TEXT_NODE) {
      const text_1 = container.textContent ?? '';
      const offset = range_0.startOffset;
      needsSpace = offset > 0 && text_1[offset - 1] !== ' ';
    }
    document.execCommand('insertText', false, needsSpace ? ' @' : '@');
    setActiveTrigger({
      char: '@',
      query: ''
    });
    setHighlightedIndex(0);
    requestAnimationFrame(() => {
      setAnchorEl(getCaretAnchor());
    });
  };
  const handlePaste = (event_0: React.ClipboardEvent<HTMLDivElement>): void => {
    event_0.preventDefault();
    if (onPasteFiles != null) {
      const files = getClipboardFilesToAttach(event_0.clipboardData);
      if (files.length > 0) {
        onPasteFiles(files);
        return;
      }
    }
    const text_2 = event_0.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text_2);
  };
  const handleLinkPopup = (): void => {
    const selection_5 = globalThis.getSelection();
    if (selection_5 != null && selection_5.rangeCount > 0) {
      savedRangeRef.current = selection_5.getRangeAt(0).cloneRange();
      setLinkInitialText(selection_5.toString());
    } else {
      savedRangeRef.current = null;
      setLinkInitialText('');
    }
    setOpenLinkPopup(true);
  };
  const renderMenuItem = (item_2: TriggerMenuItem, index: number): React.ReactElement => {
    const menuItem = <StyledTriggerMenuItem ref={index === highlightedIndex ? scrollHighlightedItemIntoView : undefined} data-highlighted={index === highlightedIndex} data-disabled={item_2.disabled === true} onMouseDown={(e: React.MouseEvent<HTMLLIElement>) => {
      e.preventDefault();
      handleMenuItemSelect(item_2);
    }}>
        {item_2.leftIcon}
        <StyledTriggerMenuItemText>
          <span>{item_2.name}</span>
          {item_2.annotation != null && item_2.annotation.length > 0 ? <StyledTriggerMenuItemAnnotation>{item_2.annotation}</StyledTriggerMenuItemAnnotation> : null}
        </StyledTriggerMenuItemText>
      </StyledTriggerMenuItem>;
    const tooltipBody = getTooltipBody(item_2.tooltip);
    if (tooltipBody == null) {
      return <React.Fragment key={item_2.id}>{menuItem}</React.Fragment>;
    }
    return <Tooltip key={item_2.id} kind="multiline" title={item_2.name} body={tooltipBody} placement="right">
        {menuItem}
      </Tooltip>;
  };
  const defaultActions = [...(mentions.length > 0 ? [<Button key="mentions" variant="text" scheme="light" leftIcon={<MentionsIcon />} onMouseDown={preventBlur} onClick={handleMentionsTrigger} />] : []), <Button key="bold" variant={isBoldActive ? 'tonal' : 'text'} scheme="light" leftIcon={<BoldIcon />} onMouseDown={preventBlur} onClick={handleBold} disabled={isEmpty} />, <Button key="italic" variant={isItalicActive ? 'tonal' : 'text'} scheme="light" leftIcon={<ItalicIcon />} onMouseDown={preventBlur} onClick={handleItalic} disabled={isEmpty} />, <Button key="link" variant="text" scheme="light" leftIcon={<ChainLinkIcon />} onMouseDown={preventBlur} onClick={handleLinkPopup} disabled={isEmpty} />];
  const actionsWithDefault = shouldShowDefaultActions ? defaultActions.concat(actions ?? []) : actions ?? [];
  const canBeCancelled = onCancel !== undefined;
  return <StyledGridComposerContainer $inputHasValue={!isEmpty} $hasActions={actionsWithDefault.length > 0} $isInternalMessage={isInternalMessage} $elevated={elevated}>
      <StyledGridComposerInputContainer>
        <StyledComposerMarkdownPreview ref={editorRef} role="textbox" aria-label={inputPlaceholder} contentEditable suppressContentEditableWarning data-placeholder={inputPlaceholder} data-empty={isEmpty || undefined} onInput={handleInput} onMouseOver={handleEditorMouseOver} onMouseLeave={hideChipTooltip} onPaste={handlePaste} onMouseUp={() => {
        syncFormattingState();
        rememberCaret();
      }} onKeyUp={() => {
        syncFormattingState();
        rememberCaret();
      }} onKeyDown={(event_1: React.KeyboardEvent<HTMLDivElement>) => {
        // Empty menu (message only): nothing to select, so Enter closes it like Escape.
        if (isMenuOpen && menuItems.length === 0 && (event_1.key === 'Escape' || event_1.key === 'Enter')) {
          event_1.preventDefault();
          closeMenu();
          return;
        }
        if (isMenuOpen) {
          const nav = navigateTriggerMenu(event_1, menuItems.length, setHighlightedIndex);
          if (nav === 'handled') return;
          if (nav === 'close') {
            closeMenu();
            return;
          }
          if (nav === 'select') {
            const item_3 = menuItems[highlightedIndex];
            event_1.preventDefault();
            if (item_3 != null && item_3.disabled !== true) {
              handleMenuItemSelect(item_3);
            } else {
              // Disabled (gated) item: Enter only dismisses the menu; a second Enter submits.
              closeMenu();
            }
            return;
          }
        }
        if (event_1.key === 'Enter' && !event_1.shiftKey) {
          event_1.preventDefault();
          handleSubmit();
        }
      }} onBlur={() => {
        rememberCaret();
        setTimeout(closeMenu, 150);
      }} />
        {canBeCancelled && isSubmitting ? <Button variant="text" scheme="light" onClick={onCancel} leftIcon={<PauseIcon />} /> : <Button variant="filled" scheme="light" disabled={isEmpty || disableSubmit} onClick={handleSubmit} leftIcon={<ArrowRightIcon />} />}
      </StyledGridComposerInputContainer>
      {typeof attachmentSlot === 'function' ? attachmentSlot({
      inputHasValue: !isEmpty
    }) : attachmentSlot}
      {actionsWithDefault.length > 0 && <StyledGridComposerActionsContainer>{actionsWithDefault}</StyledGridComposerActionsContainer>}
      {activeTriggerConfig != null ? <ComposerTriggerMenu open={isMenuOpen} anchorEl={anchorEl} width={activeTriggerConfig.menuWidth} maxHeight={activeTriggerConfig.menuMaxHeight}>
          {emptyMessage != null && menuItems.length === 0 ? <StyledTriggerMenuEmpty onMouseDown={preventBlur}>{emptyMessage}</StyledTriggerMenuEmpty> : menuItems.map((item_4, index_0) => renderMenuItem(item_4, index_0))}
        </ComposerTriggerMenu> : null}
      {chipTooltip != null ? createPortal(<Tooltip kind="multiline" title={chipTooltip.command.name} body={getTooltipBody(chipTooltip.command.tooltip)} placement="top" open>
              <span aria-hidden style={{
        position: 'fixed',
        top: chipTooltip.rect.top,
        left: chipTooltip.rect.left,
        width: chipTooltip.rect.width,
        height: chipTooltip.rect.height,
        pointerEvents: 'none'
      }} />
            </Tooltip>, document.body) : null}
      <InsertLinkPopup open={openLinkPopup} onClose={() => {
      setOpenLinkPopup(false);
    }} initialText={linkInitialText} onSave={handleInsertLink} />
    </StyledGridComposerContainer>;
};
export default MessageComposer;